import {
  BreadcrumbHeading,
  Chip,
  SearchField,
  Spinner,
  Table,
} from '@weareopenr/component-library';
import styles from './Roles.module.scss';
import { useMemo, useState } from 'react';
import { IRole } from 'src/types';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { useRoles } from 'src/hooks/useRoles/useRoles';

export const Roles = () => {
  const [searchFilter, setSearchFilter] = useState<string>('');
  const { roles, isLoading } = useRoles();
  const filteredRoles = useMemo(() => {
    if (!searchFilter) {
      return roles;
    }
    return roles?.filter((role) => role.name.toLowerCase().includes(searchFilter.toLowerCase()));
  }, [roles, searchFilter]);

  if (isLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner size="large" />
      </div>
    );
  }

  const renderPermissions = (info: CellContext<IRole, string[]>) => {
    const permissions = info.row.original.permissions;
    if (!permissions || permissions.length === 0) {
      return 'Base permissions only';
    }
    return (
      <div className={styles['permissions-container']}>
        {permissions
          .toSorted((a, b) => a.localeCompare(b))
          .map((permission) => (
            <Chip key={permission} label={permission} backgroundColour="blueberry" />
          ))}
      </div>
    );
  };

  const renderRoleDescription = (info: CellContext<IRole, string>) => (
    <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
      {info.getValue() || 'No description'}
    </div>
  );

  const columnHelper = createColumnHelper<IRole>();

  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      cell: renderRoleDescription,
    }),
    columnHelper.accessor('permissions', {
      header: 'Permissions',
      cell: renderPermissions,
    }),
  ];

  return (
    <div className={styles.container}>
      <BreadcrumbHeading breadcrumbs={[{ label: 'Roles', path: '/roles' }]} />
      <div className={styles['filter-container']}>
        <SearchField
          placeholder="Filter Roles"
          onChange={(e) => setSearchFilter(e.target.value)}
          isDisabled={false}
        />
      </div>
      <div className={styles['table-container']}>
        <Table columns={columns} data={filteredRoles || []} />
      </div>
    </div>
  );
};
